import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { BenefitsCustomerSMSLogin } from '@components/Login/BenefitsCustomerSMSLogin';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { URLKeys, invalidateQueryExactMatch, noddiAsync } from 'noddi-async';
import { NoddiBasicCard, NoddiInfoBanner, useAppForm } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { z } from 'zod';

type Props = {
  slug: string;
  brandDomain: string;
};

const getValidationSchema = () =>
  z.object({
    authCode: z.string().min(1, t`Required`)
  });

type FormValues = z.input<ReturnType<typeof getValidationSchema>>;

export const ActivateMembershipCard = ({ slug, brandDomain }: Props) => {
  const [discountCode, setDiscountCode] = useState<string>();
  const { currentUserGroupId: userGroupId, isLoggedIn } = useAuthContext();

  const { mutateAsync: activateMembership, error: activateMembershipError } = noddiAsync.usePost({
    type: URLKeys.postActivateMembership,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({ urlKey: URLKeys.getMembershipFromSlug, input: { slug, userGroupId } });
      }
    }
  });

  const {
    data: verifyMembershipCodeData,
    error: verifyMembershipCodeError,
    isLoading: isGetVerifyMembershipCodeLoading
  } = noddiAsync.useGet({
    type: URLKeys.getVerifyMembershipCode,
    input: { slug, authCode: discountCode! },
    queryConfig: {
      enabled: !!discountCode
    }
  });

  const defaultValues: FormValues = {
    authCode: ''
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: getValidationSchema()
    },
    onSubmit: async ({ value }) => {
      if (userGroupId) {
        await activateMembership({
          userGroupId,
          slug,
          authCode: value.authCode,
          brandDomain
        });
      } else {
        setDiscountCode(value.authCode);
      }
    }
  });

  const activateMemberShipAfterLogin = async (userGroupId: number | null) => {
    if (userGroupId) {
      await activateMembership({
        userGroupId,
        slug,
        authCode: discountCode!,
        brandDomain
      });
    }
  };

  return (
    <>
      {!isLoggedIn && verifyMembershipCodeData?.isValid ? (
        <div className='flex flex-col gap-2'>
          <p className='font-semibold text-primary'>
            {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
            <Trans>Discount code is valid!</Trans>🥳
          </p>
          <BenefitsCustomerSMSLogin defaultOpen activateMemberShipAfterLogin={activateMemberShipAfterLogin} />
        </div>
      ) : (
        <NoddiBasicCard className='flex flex-col gap-2'>
          <p className='font-semibold text-primary'>
            {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
            <Trans>Enter discount code to connect the membership benefits to your account</Trans> 🥳
          </p>
          <form.AppForm>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              <div className='flex flex-col gap-1'>
                <form.AppField name='authCode'>{(field) => <field.Input />}</form.AppField>
                {verifyMembershipCodeData && !verifyMembershipCodeData.isValid && (
                  <NoddiInfoBanner
                    className='my-2'
                    variant='error'
                    texts={[{ value: t`Incorrect discount code, please try again` }]}
                  />
                )}
                <div className='mt-2 flex justify-end'>
                  <form.SubscribeButton label={t`Activate membership`} disabled={isGetVerifyMembershipCodeLoading} />
                </div>
              </div>
            </form>
          </form.AppForm>
        </NoddiBasicCard>
      )}

      {verifyMembershipCodeError && <ApiErrorMessageWithTrans error={verifyMembershipCodeError} />}
      {activateMembershipError && <ApiErrorMessageWithTrans error={activateMembershipError} />}
    </>
  );
};
